/**
 * Comprime imagem no browser (Canvas) antes do upload.
 * Sem dependências externas. Se falhar ou não reduzir, devolve o arquivo original.
 */

export const IMAGE_MAX_EDGE_PX = 1920;
export const IMAGE_JPEG_QUALITY = 0.8;

function isImageFile(file) {
  return Boolean(file?.type?.startsWith("image/"));
}

function extensionForMime(mime) {
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  return "jpg";
}

function outputMime(file) {
  // JPEG costuma comprimir melhor para foto de celular; PNG só se já for PNG pequeno com transparência
  if (file.type === "image/png") return "image/jpeg";
  if (file.type === "image/webp") return "image/webp";
  return "image/jpeg";
}

function scaledSize(width, height, maxEdge) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width, height };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("toBlob retornou vazio"));
          return;
        }
        resolve(blob);
      },
      mime,
      quality
    );
  });
}

/**
 * @param {File} file
 * @param {{ maxEdge?: number, quality?: number }} [options]
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file || !isImageFile(file)) {
    return file;
  }

  const maxEdge = options.maxEdge ?? IMAGE_MAX_EDGE_PX;
  const quality = options.quality ?? IMAGE_JPEG_QUALITY;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = scaledSize(bitmap.width, bitmap.height, maxEdge);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const mime = outputMime(file);
    const blob = await canvasToBlob(canvas, mime, quality);

    if (blob.size >= file.size) {
      return file;
    }

    const baseName = (file.name || "foto").replace(/\.[^.]+$/, "");
    const name = `${baseName}.${extensionForMime(mime)}`;
    return new File([blob], name, { type: mime, lastModified: Date.now() });
  } catch {
    return file;
  }
}
